<script>
import { mapState } from 'vuex';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import ContactModel from '@models/Contact';
import CompanyModel from '@models/Company';
import isFunction from 'lodash/isFunction';
import EntityMultiselect from '@components/EntityMultiselect';
import { quillEditor } from 'vue-quill-editor';
import datePickerUtility from '@mixins/datePickerUtility';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

export default {
  components: {
    BlockingContainer,
    quillEditor,
    EntityMultiselect,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    companyId: {
      default: null,
      required: false,
      type: Number,
    },
    isModal: {
      default: false,
      type: Boolean,
    }
  },
  data() {
    return {
      companyModel: CompanyModel,
      contact: {
        first_name: null,
        last_name: null,
        email: null,
        mobile_phone: null,
        phone: null,
        birthdate: null,
        company_id: null,
        notes: null,
      },
    };
  },
  computed: {
    ...mapState({
      dateOnlyConfig: (state) => state.common.dateOnlyConfig,
      quillOptions: (state) => state.common.quillOptions,
    }),
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('contact loading');
      ContactModel.find(this.loadedId).then((response) => {
        this.contact = Object.assign({}, this.contact, response.contact);
        this.$wait.end('contact loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['contact'], this.validate);
        });
      });
    } else if(this.companyId) {
        this.$wait.start('company loading');
        this.companyModel.find(this.companyId).then((response) => {
          this.contact.company = Object.assign({}, response.company);
          this.contact.company_id = response.company.id;
          this.$wait.end('company loading');
        });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('contact loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (!this.isModal && this.$route.params.id) {
        this.contact.id = this.$route.params.id;
      }
      new ContactModel(this.contact)
        .save()
        .then((response) => {
          this.$emit('submitted', response.contact);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('contact loading');
        })
        .catch(() => this.$wait.end('contact loading'));
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('contact loading')"></BlockingContainer>
    <b-form @submit.prevent="validate">
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.first_name') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="first_name"
          v-model="contact.first_name"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="first_name"
          :data-vv-as="$t('fields.first_name')"
          type="text"
          :state="errors.has('first_name') ? false : null"
          aria-describedby="input-feedback-first_name"
          :placeholder="$t('placeholders.first_name')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-first_name">
          <template v-show="errors.has('first_name')">{{ errors.first('first_name') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.last_name') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="last_name"
          v-model="contact.last_name"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="last_name"
          :data-vv-as="$t('fields.last_name')"
          type="text"
          :state="errors.has('last_name') ? false : null"
          aria-describedby="input-feedback-last_name"
          :placeholder="$t('placeholders.last_name')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-last_name">
          <template v-show="errors.has('last_name')">{{ errors.first('last_name') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.email') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="email"
          v-model="contact.email"
          v-validate="'required|email|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="email"
          :data-vv-as="$t('fields.email')"
          type="text"
          :state="errors.has('email') ? false : null"
          aria-describedby="input-feedback-email"
          :placeholder="$t('placeholders.email')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-email">
          <template v-show="errors.has('email')">{{ errors.first('email') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.phone')}:`">
        <b-form-input
          id="phone"
          v-model="contact.phone"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="phone"
          :data-vv-as="$t('fields.phone')"
          type="text"
          :state="errors.has('phone') ? false : null"
          aria-describedby="input-feedback-phone"
          :placeholder="$t('placeholders.phone')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-mobile_phone">
          <template v-show="errors.has('mobile_phone')">{{ errors.first('mobile_phone') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.mobile_phone')}:`">
        <b-form-input
          id="mobile_phone"
          v-model="contact.mobile_phone"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="mobile_phone"
          :data-vv-as="$t('fields.mobile_phone')"
          type="text"
          :state="errors.has('mobile_phone') ? false : null"
          aria-describedby="input-feedback-mobile_phone"
          :placeholder="$t('placeholders.mobile_phone')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-mobile_phone">
          <template v-show="errors.has('mobile_phone')">{{ errors.first('mobile_phone') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.company')}:`">
        <EntityMultiselect
          v-model="contact.company_id"
          v-validate="''"
          data-vv-name="company_id"
          :data-vv-as="$t('fields.company')"
          :initial-value="contact.company"
          :state="errors.has('company_id')"
          :placeholder="$t('placeholders.company')"
          :model="companyModel"
        >
        </EntityMultiselect>
        <b-form-invalid-feedback id="input-feedback-company_id">
          <template v-show="errors.has('company_id')">{{ errors.first('company_id') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.notes')}:`">
        <quill-editor ref="notes" v-model="contact.notes" :options="quillOptions" />
      </b-form-group>
      <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
